/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/

import MsDyn365, { ICoreContext, IDateFormatOptions, IRequestContext } from '@msdyn365-commerce/core';
import { BusinessPartnerOperationRequest, BusinessPartnerOperationRequestDetails } from '@msdyn365-commerce/retail-proxy';
import { ArrayExtensions, StringExtensions } from '@msdyn365-commerce-modules/retail-actions';
import { IDataTableProps, IHeadingsProperty, ITableItemProps, ITableRowProps, Table, TableDataType } from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';

import { IB2bRequestsStatusData } from '../b2b-requests-status.data';
import { IB2bRequestsStatusProps } from '../b2b-requests-status.props.autogenerated';

export interface IB2bRequestsTableProps {
    moduleProps: IB2bRequestsStatusProps<IB2bRequestsStatusData>;
    isMobile: boolean;
    mobileExcludedColumns: string[];
}

export interface IB2bRequestsTableResources {
    b2bRequestsTypeCreateProspect: string;
    b2bRequestsTypeAddUser: string;
    b2bRequestsTypeEditUser: string;
    b2bRequestsTypeDeleteUser: string;
    b2bRequestsTypeRequestAccountStatement: string;
    b2bRequestsTypeRequestInvoiceCopy: string;
    b2bRequestsTypeUnknown: string;
    b2bRequestsStatusRequested: string;
    b2bRequestsStatusProcessed: string;
    b2bRequestsStatusError: string;
    b2bRequestsNumberOfItems: string;
    b2bRequestsNumberOfItemsSingular: string;
    b2bRequestsNameDisplay: string;
    b2bRequestsPreviousText: string;
    b2bRequestsNextText: string;
    b2bRequestsUserColumn: string;
    b2bRequestsStatusColumn: string;
    b2bRequestsRequestDateColumn: string;
    b2bRequestsTypeColumn: string;
    b2bRequestsDescriptionColumn: string;
    b2bRequestsAccountStatementDetails: string;
    b2bRequestsInvoiceCopyDetails: string;
    businessUserSelectCheckBoxAriaLabelText?: string;
}

export enum RequestsTableColumnHeader {
    Name = 'User',
    Status = 'Status',
    RequestDate = 'Request date',
    Type = 'Request type',
    Description = 'Request detail'
}

const getCurrentUrl = (reqContext: IRequestContext): URL => {
    if (MsDyn365.isBrowser) {
        return new URL(window.location.href);
    }

    // NOTE: Typing on requestURL is incorrect
    if (reqContext.url.requestUrl.href) {
        return new URL(reqContext.url.requestUrl.href);
    }
    return new URL(reqContext.url.requestUrl.href);

};

const getUserName = (request: BusinessPartnerOperationRequest, resources: IB2bRequestsTableResources) => {
    if (StringExtensions.isNullOrEmpty(request.UserFirstName)) {
        if (StringExtensions.isNullOrEmpty(request.UserLastName)) {
            return '';
        }

        return request.UserLastName;
    } else if (StringExtensions.isNullOrEmpty(request.UserLastName)) {
        return request.UserFirstName;
    }

    return resources.b2bRequestsNameDisplay
        .replace('{0}', request.UserFirstName!)
        .replace('{1}', request.UserLastName!);

};

const formatDate = (context: ICoreContext, date: Date | undefined) => {
    if (date === undefined) {
        return '';
    }

    const dateOptions: IDateFormatOptions = { year: 'numeric', month: 'long', day: 'numeric' };
    return context.cultureFormatter.formatDate(date, dateOptions);
};

/**
 * Gets the value to display for the request details.
 * @param request - The request to parse for the details.
 * @param resources - The resources.
 * @param context - The context.
 * @returns The string to display.
 */
const getDetails = (request: BusinessPartnerOperationRequest, resources: IB2bRequestsTableResources, context: ICoreContext) => {

    if (!request.Details) {
        return '';
    }

    const requestDetails: BusinessPartnerOperationRequestDetails = request.Details;

    const accountStatementStartDateTime = formatDate(context, requestDetails.AccountStatementStartDateTime) || '';
    const accountStatementEndDateTime = formatDate(context, requestDetails.AccountStatementEndDateTime) || '';
    const invoiceId = requestDetails.InvoiceId ?? '';

    switch (request.TypeValue) {
        // eslint-disable-next-line @typescript-eslint/no-magic-numbers -- workaround for not being able to use enum value
        case 5:
            if (!StringExtensions.isNullOrEmpty(accountStatementStartDateTime) && !StringExtensions.isNullOrEmpty(accountStatementEndDateTime)) {
                return resources.b2bRequestsAccountStatementDetails
                    .replace('{0}', accountStatementStartDateTime)
                    .replace('{1}', accountStatementEndDateTime);
            }

            return '';
        // eslint-disable-next-line @typescript-eslint/no-magic-numbers -- workaround for not being able to use enum value
        case 6:
            if (!StringExtensions.isNullOrEmpty(invoiceId)) {
                return resources.b2bRequestsInvoiceCopyDetails
                    .replace('{0}', invoiceId);
            }

            return '';
        default:
            return '';
    }
};

const createTableItems = (operationRequests: BusinessPartnerOperationRequest[] | undefined, props: IB2bRequestsStatusProps<IB2bRequestsStatusData>): ITableRowProps[] => {

    if (!ArrayExtensions.hasElements(operationRequests)) {
        return [];
    }

    return operationRequests.map((request) => {

        return {
            row: [
                {
                    id: props.resources.b2bRequestsTypeColumn,
                    type: TableDataType.Text,
                    value: getDisplayFriendlyTypeValue(request.TypeValue ?? 0, props.resources)
                } as ITableItemProps,
                {
                    id: props.resources.b2bRequestsRequestDateColumn,
                    type: TableDataType.Text,
                    value: formatDate(props.context, request.CreatedDateTime)
                } as ITableItemProps,
                {
                    id: props.resources.b2bRequestsDescriptionColumn,
                    type: TableDataType.Text,
                    value: getDetails(request, props.resources, props.context)
                } as ITableItemProps,
                {
                    id: props.resources.b2bRequestsUserColumn,
                    type: TableDataType.Text,
                    value: getUserName(request, props.resources)
                } as ITableItemProps,
                {
                    id: props.resources.b2bRequestsStatusColumn,
                    type: TableDataType.Text,
                    value: getDisplayFriendlyStatusValue(request.StatusValue ?? 0, props.resources)
                } as ITableItemProps
            ],
            isSelected: false,
            className: props.config.className
        };
    });
};

const getDisplayFriendlyTypeValue = (typeValue: number, resources: IB2bRequestsTableResources): string => {
    switch (typeValue) {
        case 1:
            return resources.b2bRequestsTypeCreateProspect;
        case 2:
            return resources.b2bRequestsTypeAddUser;
        case 3:
            return resources.b2bRequestsTypeDeleteUser;
        case 4:
            return resources.b2bRequestsTypeEditUser;
        case 5:
            return resources.b2bRequestsTypeRequestAccountStatement;
        case 6:
            return resources.b2bRequestsTypeRequestInvoiceCopy;
        default:
            return resources.b2bRequestsTypeUnknown;
    }
};

const getDisplayFriendlyStatusValue = (statusValue: number, resources: IB2bRequestsTableResources): string => {
    switch (statusValue) {
        case 1:
            return resources.b2bRequestsStatusProcessed;
        case 2:
            return resources.b2bRequestsStatusError;
        default:
            return resources.b2bRequestsStatusRequested;
    }
};

const createTableProps = (tableProps: IB2bRequestsTableProps) => {
    const { context, data, resources } = tableProps.moduleProps;

    const tableData = createTableItems(data.operationRequests.result, tableProps.moduleProps);
    const tableHeading = ArrayExtensions.hasElements(tableData) && tableData[0].row.filter((item) => {
        // Check that this is either not mobile or the column should be displayed when mobile.
        return !tableProps.isMobile || !tableProps.mobileExcludedColumns.includes(item.id);
    }).map((item) => {
        return {
            name: item.id,
            sortable: true,
            ariaLabel: resources.columnSortAriaLabel
        } as IHeadingsProperty;
    }) || [];

    return {
        resources: {},
        headings: tableHeading,
        rows: tableData,
        className: `${tableProps.moduleProps.config.className}__table`,
        editLinkText: '',
        deleteLinkText: '',
        viewLinkText: '',
        enableToModify: false,
        actionLinkText: '',
        showCheckBoxes: false,
        isSortable: true,
        showPagination: true,
        minifyActions: tableProps.isMobile,
        excludedColumns: tableProps.isMobile ? tableProps.mobileExcludedColumns : [],
        paginationProperty: {
            skipCount: context.request.query?.skip && !isNaN(Number.parseInt(context.request.query.skip, 10)) ? Number.parseInt(context.request.query.skip, 10) : 0,
            itemPerPage: 10,
            prevText: resources.b2bRequestsPreviousText,
            nextText: resources.b2bRequestsNextText,
            paginationText: '',
            url: context && getCurrentUrl(context.request).href || ''
        },
        isSingleSelectOnly: true,
        actions: {
            onDelete: undefined,
            onEdit: undefined,
            onView: undefined
        },
        businessUserSelectCheckBoxAriaLabelText: tableProps.moduleProps.resources.businessUserSelectCheckBoxAriaLabelText
    } as IDataTableProps;
};

export const B2bRequestsTable: React.FC<IB2bRequestsTableProps> = (tableProps: IB2bRequestsTableProps) => {
    return <Table {...createTableProps(tableProps)} />;
};
